import { apiService } from '@/api/apiService'

export const OAUTH_SCOPE = {
  READ: 'read',
  WRITE: 'write',
}

export const OAUTH_ERROR = {
  INVALID_LINK: 'invalid_link',
  SIGNED_OUT: 'signed_out',
  REQUEST_NOT_FOUND: 'request_not_found',
  INTERNAL_ACCOUNT: 'internal_account',
  OAUTH_DISABLED: 'oauth_disabled',
  GRANT_NOT_FOUND: 'grant_not_found',
  UNKNOWN: 'unknown',
}

const FORBIDDEN_KINDS = [OAUTH_ERROR.INTERNAL_ACCOUNT, OAUTH_ERROR.OAUTH_DISABLED]

// The request id comes from the page URL, so encode it to keep it a single path segment.
const oauthRequestUrl = (requestId) =>
  `${apiService.auth}/oauth/requests/${encodeURIComponent(requestId)}`

const oauthGrantUrl = (grantId) => `${apiService.auth}/oauth/grants/${encodeURIComponent(grantId)}`

// Takes the session the login saved or currentUser, which carry the same fields. The consent page
// has only the saved session, since currentUser is hydrated only on protected routes. The headers
// only authenticate the session: auth-service works from the user's own records, whichever org is
// sent here.
const sessionHeaders = (savedUser) => ({
  'Content-Type': 'application/json',
  'auth-type': 'web',
  token: savedUser.token,
  username: savedUser.username,
  organizationid: savedUser.own_organization_id ?? savedUser.organizationid,
})

const readError = async (response, notFoundKind) => {
  if (response.status === 401) {
    return { kind: OAUTH_ERROR.SIGNED_OUT }
  }

  if (response.status === 404) {
    return { kind: notFoundKind }
  }

  if (response.status === 403) {
    const { detail } = await response.json()

    if (FORBIDDEN_KINDS.includes(detail?.kind)) {
      return { kind: detail.kind }
    }
  }

  return { kind: OAUTH_ERROR.UNKNOWN }
}

const sendOAuth = async (url, savedUser, init, notFoundKind = OAUTH_ERROR.UNKNOWN) => {
  try {
    const response = await fetch(url, { ...init, headers: sessionHeaders(savedUser) })

    if (response.status === 204) {
      return { data: null }
    }

    if (response.ok) {
      return { data: await response.json() }
    }

    return { error: await readError(response, notFoundKind) }
  } catch (error) {
    console.error(error)

    return { error: { kind: OAUTH_ERROR.UNKNOWN } }
  }
}

const requestOAuth = (requestId, savedUser, init) =>
  sendOAuth(oauthRequestUrl(requestId), savedUser, init, OAUTH_ERROR.REQUEST_NOT_FOUND)

/**
 * Ends the saved session on auth-service. A failure is only logged: the caller forgets the session
 * locally either way.
 * @param {object} savedUser The session saved in localStorage['User']
 */
export const signOutSavedUser = async (savedUser) => {
  try {
    await fetch(`${apiService.auth}/api/logout`, {
      method: 'POST',
      headers: { 'auth-type': 'web', token: savedUser.token },
    })
  } catch (error) {
    console.error(error)
  }
}

/**
 * Loads a pending consent request from auth-service.
 * @param {string} requestId The `request` param auth-service put on the consent URL
 * @param {object} savedUser The session saved in localStorage['User']
 * @returns {Promise<{ data?: object, error?: { kind: string } }>} `data` carries app_name,
 *   redirect_host, scopes, organizations and expires_at; `error.kind` is an OAUTH_ERROR value
 */
export const fetchOAuthRequest = (requestId, savedUser) =>
  requestOAuth(requestId, savedUser, { method: 'GET' })

/**
 * Sends the user's decision on a consent request. The request is single use.
 * @param {string} requestId The `request` param auth-service put on the consent URL
 * @param {object} savedUser The session saved in localStorage['User']
 * @param {{ approve: boolean, scopes?: string[] }} decision
 * @returns {Promise<{ data?: { redirect_to: string }, error?: { kind: string } }>}
 */
export const submitOAuthDecision = (requestId, savedUser, decision) =>
  requestOAuth(requestId, savedUser, { method: 'POST', body: JSON.stringify(decision) })

/**
 * Lists the apps the signed-in user has connected: grants that are neither revoked nor expired.
 * @param {object} user currentUser, or the session saved in localStorage['User']
 * @returns {Promise<{ data?: { grants: object[] }, error?: { kind: string } }>} each grant carries
 *   id, app_name, redirect_host (null for Kodi), scopes, created_at and expires_at
 */
export const fetchOAuthGrants = (user) =>
  sendOAuth(`${apiService.auth}/oauth/grants`, user, { method: 'GET' })

/**
 * Disconnects one of the signed-in user's apps. Its access ends at once, mid-run included.
 * @param {string} grantId
 * @param {object} user currentUser, or the session saved in localStorage['User']
 * @returns {Promise<{ data?: null, error?: { kind: string } }>} `error.kind` is GRANT_NOT_FOUND
 *   when the grant doesn't exist or isn't the user's
 */
export const revokeOAuthGrant = (grantId, user) =>
  sendOAuth(oauthGrantUrl(grantId), user, { method: 'DELETE' }, OAUTH_ERROR.GRANT_NOT_FOUND)
